<script>
export default {
    name: 'AppHeader',
};
</script>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useStore } from '@/store/index.js';
import AppSidebarButton from '@/components/app/sidebar/button.vue';
import HeaderBreadcrumbs from '@/components/app/header/breadcrumbs';
import HeaderLogin from '@/components/app/header/navigation/login';
import HeaderUser from '@/components/app/header/user';
import HeaderNavigation from '@/components/app/header/navigation';
import { forEachAppModule } from '@/modules/registry';
import { emitter } from '@/utils/event/emitter.ts';
import { APP } from '@/utils/event/constants.ts';
import useSupportChat from '@/composables/useSupportChat.ts';

const store = useStore();

const showMobileMenu = ref(false);
const moduleHeaderComponents = ref([]);

const userExists = computed(() => store.getters['user/isExists']);

function initModuleHeaderComponents() {
    const components = [];

    forEachAppModule((module, name) => {
        (module.header ?? []).forEach((component, index) => {
            components.push({
                key: `${name}-${index}`,
                component,
            });
        });
    });

    moduleHeaderComponents.value = components;
}

initModuleHeaderComponents();

emitter.on(APP.MODULES, initModuleHeaderComponents);

onBeforeUnmount(() => {
    emitter.off(APP.MODULES, initModuleHeaderComponents);
});

const {
    ready,
    toggle,
} = useSupportChat();
</script>

<template>
    <header :data-test="dataTest()" class="@container/header">
        <div class="flex h-12 items-center justify-between gap-x-4 pr-2">
            <app-sidebar-button class="ml-4" />
            <header-breadcrumbs class="flex-1" />
            <div class="flex items-center gap-x-3">
                <div class="flex items-center gap-x-3">
                    <component
                        :is="component"
                        v-for="{ key, component } in moduleHeaderComponents"
                        :key="key"
                    />

                    <base-button
                        v-if="ready"
                        custom-type-class="text-white hover:bg-white/10 active:bg-white/5 disabled:text-gray-200 disabled:bg-transparent"
                        custom-size-class="px-3 py-1.5"
                        content-class="hidden @2xl/header:flex"
                        label="Help"
                        text
                        :text-transform="false"
                        :icon="{
                            class: 'text-xl',
                            name: 'headset',
                        }"
                        @click="toggle"
                    />
                </div>

                <div class="flex shrink-0 items-center justify-end gap-x-2 md:hidden">
                    <button
                        class="inline-flex cursor-pointer items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100/10 hover:text-gray-50 focus:outline-hidden"
                        @click="showMobileMenu = !showMobileMenu"
                    >
                        <svg
                            class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24"
                            :class="showMobileMenu ? 'hidden' : 'block'"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        <svg
                            class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24"
                            :class="showMobileMenu ? 'block' : 'hidden'"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="hidden shrink-0 md:flex md:items-center md:justify-end">
                    <header-login v-if="!userExists" />
                    <header-user v-else />
                </div>
            </div>
        </div>
        <div v-if="showMobileMenu" class="bg-white md:hidden">
            <header-navigation />
        </div>
    </header>
</template>
